import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Scissors-cut clip-path presets.
 * Each polygon has slightly wobbly edges — like hand-cut paper.
 * Points travel clockwise: top → right → bottom → left.
 */
const CUTS = [
  // variant 0 — gentle wobble
  `polygon(
    0.8% 1.2%, 8% 0.3%, 16% 1.5%, 24% 0.1%, 33% 1.3%, 42% 0.4%, 50% 1.1%, 58% 0.2%, 67% 1.4%, 75% 0.3%, 83% 1.2%, 92% 0.5%, 99.2% 1%,
    99% 8%, 100% 16%, 99.3% 25%, 100% 33%, 99.5% 42%, 100% 50%, 99.3% 58%, 100% 67%, 99.5% 75%, 100% 83%, 99.2% 92%, 99.5% 99%,
    92% 99.5%, 83% 100%, 75% 99.3%, 67% 100%, 58% 99.5%, 50% 99.8%, 42% 99.2%, 33% 100%, 24% 99.5%, 16% 100%, 8% 99.3%, 0.5% 99.2%,
    1% 92%, 0% 83%, 0.8% 75%, 0% 67%, 0.5% 58%, 0% 50%, 0.8% 42%, 0% 33%, 0.5% 25%, 0% 16%, 0.8% 8%
  )`,
  // variant 1 — slightly different wobble pattern
  `polygon(
    1.2% 0.8%, 10% 1.4%, 18% 0.2%, 27% 1.1%, 36% 0.5%, 45% 1.3%, 54% 0.1%, 63% 1.2%, 72% 0.4%, 81% 1.5%, 90% 0.3%, 98.8% 1.2%,
    99.5% 10%, 99% 18%, 100% 27%, 99.2% 36%, 100% 45%, 99.5% 54%, 100% 63%, 99.2% 72%, 100% 81%, 99.5% 90%, 99% 99.2%,
    90% 99.8%, 81% 99%, 72% 100%, 63% 99.3%, 54% 100%, 45% 99.5%, 36% 99.8%, 27% 99%, 18% 100%, 10% 99.5%, 1% 99.5%,
    0.5% 90%, 1% 81%, 0% 72%, 0.8% 63%, 0% 54%, 0.5% 45%, 0% 36%, 1% 27%, 0% 18%, 0.5% 10%
  )`,
  // variant 2 — more jagged, like dull scissors
  `polygon(
    1.5% 0.5%, 7% 1.8%, 14% 0%, 21% 1.5%, 28% 0.3%, 35% 1.8%, 43% 0.2%, 50% 1.5%, 57% 0%, 64% 1.8%, 71% 0.3%, 78% 1.5%, 85% 0%, 93% 1.8%, 98.5% 0.8%,
    99% 7%, 100% 14%, 98.8% 21%, 100% 28%, 98.5% 35%, 100% 43%, 98.8% 50%, 100% 57%, 98.5% 64%, 100% 71%, 99% 78%, 100% 85%, 98.8% 93%, 99.2% 99%,
    93% 98.5%, 85% 100%, 78% 98.8%, 71% 100%, 64% 98.5%, 57% 100%, 50% 98.8%, 43% 100%, 35% 98.5%, 28% 100%, 21% 99%, 14% 100%, 7% 98.5%, 1% 99.5%,
    0% 93%, 1.5% 85%, 0% 78%, 1.2% 71%, 0% 64%, 1.5% 57%, 0% 50%, 1.2% 43%, 0% 35%, 1.5% 28%, 0% 21%, 1.2% 14%, 0% 7%
  )`,
  // variant 3 — subtle, slightly cleaner cut
  `polygon(
    0.5% 0.8%, 12% 0.2%, 25% 0.9%, 37% 0.1%, 50% 0.7%, 62% 0.2%, 75% 0.8%, 87% 0.1%, 99.5% 0.5%,
    100% 12%, 99.5% 25%, 100% 37%, 99.6% 50%, 100% 62%, 99.5% 75%, 100% 87%, 99.5% 99.5%,
    87% 100%, 75% 99.5%, 62% 100%, 50% 99.6%, 37% 100%, 25% 99.5%, 12% 100%, 0.5% 99.5%,
    0% 87%, 0.5% 75%, 0% 62%, 0.4% 50%, 0% 37%, 0.5% 25%, 0% 12%
  )`,
];

/**
 * CutOut — a wrapper that makes its content look like it was cut with scissors.
 *
 * Props:
 *  - variant: 0-3 (clip-path style) or "random" to pick from content hash
 *  - shadow: color string for the drop-shadow, or false to disable
 *  - as: HTML element tag (default "div")
 *  - className: additional classes
 *  - All other props forwarded to the element
 */
const CutOut = React.forwardRef(
  (
    {
      className,
      variant = 0,
      shadow = 'var(--cutout-ink)',
      as: Tag = 'div',
      style,
      children,
      ...props
    },
    ref
  ) => {
    // Pick a variant
    const idx =
      variant === 'random'
        ? Math.abs(hashCode(String(children))) % CUTS.length
        : Math.min(variant, CUTS.length - 1);

    const clipPath = CUTS[idx];

    const combinedStyle = {
      clipPath,
      WebkitClipPath: clipPath,
      filter: shadow ? `drop-shadow(4px 4px 0px ${shadow})` : undefined,
      ...style,
    };

    return (
      <Tag ref={ref} className={cn('bg-[#fefcf6]', className)} style={combinedStyle} {...props}>
        {children}
      </Tag>
    );
  }
);
CutOut.displayName = 'CutOut';

// Simple string hash for deterministic "random" variant selection
function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

export { CutOut, CUTS };
export default CutOut;
